<template id="limited-item-list">
  <ion-item button :lines="startLines" ref="showLessButton" v-if="shownList.notShownEntryNumberStart > 0" class="load-items-button-item more" @click="showLessItems">
    <ion-label>
      <h5 class="inline-icon wl-semibold">
        {{ shownList.notShownEntryNumberStart }}
        {{ i18n.$t(`limits.${displayType}.${(shownList.notShownEntryNumberStart == 1) ? 'singular' : 'plural'}`) }}
        <ion-icon :icon="chevronUp"></ion-icon>
      </h5>
      <p class="show-action-text fl-s1">{{ i18n.$t('limits.show_more') }}</p>
    </ion-label>
  </ion-item>
  <!-- Take incoming props and bind them to the inner repeated element-->
  <template v-for="(item, itemIndexOrKey) of shownList.items" :key="((useItemAsKey) ? item : itemIndexOrKey)">
    <slot :item="item" :itemIndexOrKey="itemIndexOrKey" :itemRepeatKey="((useItemAsKey) ? item : itemIndexOrKey)"></slot>
  </template>
  <ion-item button :lines="endLines" ref="showMoreButton" v-if="shownList.notShownEntryNumberEnd > 0" class="load-items-button-item more" @click="showMoreItems">
    <ion-label>
      <h5 class="inline-icon wl-semibold">
        {{ shownList.notShownEntryNumberEnd }}
        {{ i18n.$t(`limits.${displayType}.${(shownList.notShownEntryNumberEnd == 1) ? 'singular' : 'plural'}`) }}
        <ion-icon :icon="chevronDown"></ion-icon></h5>
      <p class="show-action-text fl-s1">{{ i18n.$t('limits.show_more') }}</p>
    </ion-label>
  </ion-item>
</template>

<script>
import { IonItem, IonLabel, IonIcon } from '@ionic/vue';
import { defineComponent, computed, ref, watch } from 'vue';

import { useI18n } from "@/utils/i18n";

import { chevronUp, chevronDown } from 'ionicons/icons';

import { findNearestScrollParent } from '@/utils/interaction';

import _ from 'lodash';

export default defineComponent({
  name: 'LimitedItemList',
  components: { IonItem, IonLabel, IonIcon },
  props: {
    'items': [Array, Object],
    'useItemAsKey': {
      type: Boolean,
      default: false
    },
    'minimumVisibleItems': {
      type: Number,
      default: 10
    },
    'maximumVisibleItems': {
      type: Number,
      default: -1 //Negative number means no upper limit. If there is an upper limit the first items will get hidden and have to be loaded again
    },
    'enableCorrectionScroll': { //When maximum is reached, scrolls to the last visible item again to not disturb the view
      type: Boolean,
      default: true
    },
    'visibleItemIncrement': {
      type: Number,
      default: 10
    },
    'startLines': {
      type: String,
      default: 'full'
    },
    'endLines': {
      type: String,
      default: 'full'
    },
    'displayType': {
      type: String,
      default: 'entry'
    }
  },
  setup(props) {
    const i18n = useI18n();

    const showMoreButton = ref(null);

    const showLessButton = ref(null);

    const firstVisibleIndex = ref(0);

    const lastVisibleIndex = ref(props.minimumVisibleItems); //Excluding this index, so it represents the number of visible items in the beginning

    //On change of the list, reset view
    watch(() => props.items, (newItems, oldItems) => {
      //Actually check if the list or object changed. Then on navigation it keeps the scroll position, because list doesn't actually change!
      if (_.isEqual(oldItems, newItems)) return;
      firstVisibleIndex.value = 0;
      lastVisibleIndex.value = props.minimumVisibleItems;
    });

    const increment = computed(() => {
      //Increment can't be larger or equal to maximum visible items for scrolling to not break!
      if (props.maximumVisibleItems > 0 && props.visibleItemIncrement >= props.maximumVisibleItems) {
        return props.maximumVisibleItems - 1;
      }
      return props.visibleItemIncrement;
    });

    const showLessItems = function() {
      lastVisibleIndex.value = lastVisibleIndex.value - increment.value;
    }

    const showMoreItems = function() {
      lastVisibleIndex.value = lastVisibleIndex.value + increment.value;
    }

    const shownList = computed(() => {
      if (props.items == null) return { items: [], notShownEntryNumberEnd: 0, notShownEntryNumberStart: 0 };

      let itemsToBeSliced = [];
      //If it was an object, pick the keys again
      let transformBackToObject = false;

      if (Array.isArray(props.items)) {
        itemsToBeSliced = props.items;
      } else if (_.isObject(props.items)) {
        itemsToBeSliced = Object.keys(props.items);
        transformBackToObject = true;
      }

      let lastPreviousElement;
      let lastPreviousTop;

      let slicedItems = itemsToBeSliced.slice(firstVisibleIndex.value, lastVisibleIndex.value);
      let fullLength = slicedItems.length;
      let notShownEntryNumberEnd = itemsToBeSliced.length - fullLength;
      let notShownEntryNumberStart = 0;
      //If enabled, valid maximum and too many would display, slice from the end the maximum visible amount
      if (props.maximumVisibleItems > 0 && props.maximumVisibleItems > props.minimumVisibleItems && fullLength > props.maximumVisibleItems) {
        slicedItems = slicedItems.slice(-(props.maximumVisibleItems));
        notShownEntryNumberStart = fullLength - slicedItems.length; //Hidden is what is left in the difference after slicing again from the previous slice
        if (props.enableCorrectionScroll && showMoreButton.value != null && showMoreButton.value.$el != null) {
          lastPreviousElement = showMoreButton.value.$el.previousElementSibling;
          if (lastPreviousElement != null) {
            let dimensions = lastPreviousElement.getBoundingClientRect();
            if (dimensions != null) {
              lastPreviousTop = dimensions.top;
            }
          }
        }
      }

      if (transformBackToObject) {
        slicedItems = _.pick(props.items, slicedItems);
      }

      return {items: slicedItems, notShownEntryNumberEnd, notShownEntryNumberStart, lastPreviousElement, lastPreviousTop};
    });

    //Defer after it has been completed, because timeout is not possible inside computed
    watch(shownList, (newListProperties, oldListProperties) => {
      if (newListProperties != null && newListProperties.notShownEntryNumberStart != null && newListProperties.lastPreviousElement != null && newListProperties.lastPreviousTop != null) {
        let previousNotShownStart = (oldListProperties != null && oldListProperties.notShownEntryNumberStart != null) ? oldListProperties.notShownEntryNumberStart : 0;
        if (props.enableCorrectionScroll) {
          //Calculate the difference between two changes. By that amount the view moved, so scroll back up these many elements to end up where the view was
          let difference = newListProperties.notShownEntryNumberStart - previousNotShownStart;
          //If difference is smaller or equal to 0, scrolling will not happen or stay the same
          let previousElement = newListProperties.lastPreviousElement;
          for (let elementOffsetIndex = 0; (previousElement != null && elementOffsetIndex < difference); elementOffsetIndex++) {
            previousElement = previousElement.previousElementSibling;
          }
          //If we went too far back do not scroll
          if (previousElement == null) return;
          //Run immediately, do not defer to avoid pop-in
          let scrollParent = findNearestScrollParent(previousElement);
          let currentDimensions = previousElement.getBoundingClientRect();
          if (scrollParent != null && currentDimensions != null) {
            let currentTop = currentDimensions.top;
            if (currentTop != null) {
              const scrollDelta = currentDimensions.top - newListProperties.lastPreviousTop;
              scrollParent.scrollBy({ top: scrollDelta });
            }
          }
        }
      }
    });

    return { i18n, shownList, showLessItems, showMoreItems, showMoreButton, showLessButton, chevronUp, chevronDown };
  }
});
</script>

<style scoped>
.load-items-button-item {
  text-align: center;
  /* --border-width: 0px 0px 0px 0px; */
  /* --padding-start: min(8vw, 4rem); */
  margin-left: 0px;
}

.load-items-button-item .show-action-text {
  color: var(--ion-color-primary)!important;
}

.inline-icon {
  display: inline-flex;
  align-items: center;
  column-gap: 5px;
}
</style>